/**
 * Mar 03, 2026 · SF Master (v2) — process-aware auto-code, dedicated collection.
 *
 *   • Mandatory `Process` picker at top:  Covering · Spandex Warping · Warping
 *   • `sf_id`   auto (SFC-##### / SFSW-##### / SFW-#####) — locked on save
 *   • `sf_code` auto-composed from formula tokens — LOCKED on save
 *       Covering        → {SpandexDenier}D{Stretch}X-{RM-Category}-{YarnDenier}D-{Colour}
 *       Spandex Warping → {SpandexDenier}D{Stretch}X-{Colour}
 *       Warping         → {RM-Category}-{YarnDenier}D-{Colour}
 *
 * Endpoints
 *   GET    /api/sf-master/           — list
 *   POST   /api/sf-master/           — create (auto sf_id + sf_code, LOCKED)
 *   PUT    /api/sf-master/{id}       — mutable subset (identity locked)
 *   POST   /api/sf-master/generate   — live sf_code preview (no persist)
 *   DELETE /api/sf-master/{id}       — guarded
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { RowActions } from "@/components/RowActions";
import { toast } from "sonner";
import {
  PlusIcon, TrashIcon, PencilSimpleIcon, MagnifyingGlassIcon,
  FloppyDiskIcon, XIcon, DownloadSimpleIcon, LockKeyIcon,
  MagicWandIcon,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { exportToCSV } from "@/lib/csv";

const PROCESSES = ["Covering", "Spandex Warping", "Warping"];
const PROCESS_PREFIX = { "Covering": "SFC", "Spandex Warping": "SFSW", "Warping": "SFW" };

const inp = "w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";
const inpNum = inp + " font-mono tabular-nums text-right";
const btnPrimary = "inline-flex items-center gap-1.5 h-9 px-4 bg-[var(--accent)] text-white text-xs font-mono uppercase tracking-widest rounded-sm hover:opacity-90 transition";
const btnGhost = "inline-flex items-center gap-1.5 h-9 px-4 border border-[var(--border)] text-xs font-mono uppercase tracking-widest rounded-sm hover:bg-[var(--bg-2)] transition";

const emptyForm = {
  process: "Covering",
  spandex_denier: "", stretch: "", rm_category: "", yarn_denier: "", colour: "",
  covering_type: "", spandex_pct: 0, yarn_pct: 0,
  output_uom: "kg", hs_code: "", description: "", is_active: true,
};

// Local preview mirrors the backend `compose_sf_code`; kept in sync so the
// operator sees the same code the API would produce even before it lands
// (avoids a request per keystroke).
function previewSfCode(f) {
  const norm = (v) => String(v || "").trim().toUpperCase().split(/\s+/).join("-");
  const denier = (v) => { const s = String(v || "").trim().toUpperCase(); return s ? (s.endsWith("D") ? s : s + "D") : ""; };
  const stretch = (v) => { const s = String(v || "").trim().toUpperCase(); return s ? (s.endsWith("X") ? s : s + "X") : ""; };
  const sd = denier(f.spandex_denier), st = stretch(f.stretch);
  const rc = norm(f.rm_category), yd = denier(f.yarn_denier), cl = norm(f.colour);
  const head = (sd && st) ? sd + st : (sd || st);
  if (f.process === "Covering")        return [head, rc, yd, cl].filter(Boolean).join("-");
  if (f.process === "Spandex Warping") return [head, cl].filter(Boolean).join("-");
  if (f.process === "Warping")         return [rc, yd, cl].filter(Boolean).join("-");
  return "";
}

export default function SFMaster() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("active"); // active | inactive | all
  const [processFilter, setProcessFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  // Phase 3J.6 · BOM-driven Warping SFG dialog state
  const [bomDialogOpen, setBomDialogOpen] = useState(false);
  const [warpingCwos, setWarpingCwos] = useState([]);
  const [selectedCwoId, setSelectedCwoId] = useState("");
  const [bomPreview, setBomPreview] = useState(null);
  const [selectedLineIds, setSelectedLineIds] = useState({});
  const [bomLoading, setBomLoading] = useState(false);
  const [bomGenerating, setBomGenerating] = useState(false);
  const [bomResult, setBomResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sf-master/");
      setList(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load SF Master");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Phase 3J.6 · when the BOM dialog opens, load Warping CWOs once so
  // the owner sees the picker populated.
  useEffect(() => {
    if (!bomDialogOpen) return;
    (async () => {
      try {
        const { data } = await api.get("/child-work-orders");
        const rows = Array.isArray(data) ? data : (data.rows || []);
        setWarpingCwos(rows.filter(r => (r.stage_code || "").toUpperCase() === "WARPING"));
      } catch { toast.error("Failed to load Warping CWOs"); }
    })();
  }, [bomDialogOpen]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return list.filter(a => {
      if (statusFilter === "active" && a.is_active === false) return false;
      if (statusFilter === "inactive" && a.is_active !== false) return false;
      if (processFilter && a.process !== processFilter) return false;
      if (!needle) return true;
      return [a.sf_code, a.sf_id, a.description, a.rm_category, a.colour, a.process]
        .some(v => String(v || "").toLowerCase().includes(needle));
    });
  }, [list, q, statusFilter, processFilter]);

  const activeCount = list.filter(a => a.is_active !== false).length;
  const inactiveCount = list.length - activeCount;

  const openNew = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      process: a.process || "Covering",
      spandex_denier: a.spandex_denier || "",
      stretch: a.stretch || "",
      rm_category: a.rm_category || "",
      yarn_denier: a.yarn_denier || "",
      colour: a.colour || "",
      covering_type: a.covering_type || "",
      spandex_pct: a.spandex_pct || 0,
      yarn_pct: a.yarn_pct || 0,
      output_uom: a.output_uom || "kg",
      hs_code: a.hs_code || "",
      description: a.description || "",
      is_active: a.is_active !== false,
    });
    setDialogOpen(true);
  };

  const composed = useMemo(() => previewSfCode(form), [form]);
  const composedPrefix = PROCESS_PREFIX[form.process] || "";

  const save = async () => {
    if (!PROCESSES.includes(form.process)) return toast.error("Pick a process first");
    setSaving(true);
    try {
      if (editing) {
        // Identity (process / sf_id / sf_code) is locked — send mutable subset only.
        await api.put(`/sf-master/${editing.id}`, {
          description: form.description,
          output_uom: form.output_uom,
          covering_type: form.covering_type,
          spandex_pct: Number(form.spandex_pct) || 0,
          yarn_pct: Number(form.yarn_pct) || 0,
          hs_code: form.hs_code,
          is_active: !!form.is_active,
        });
        toast.success(`Updated · ${editing.sf_code}`);
      } else {
        const { data } = await api.post("/sf-master/", form);
        toast.success(`Created · ${data.sf_id} · ${data.sf_code}`);
      }
      setDialogOpen(false);
      setForm(emptyForm);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete SF ${a.sf_code} (${a.sf_id})? This cannot be undone.`)) return;
    try {
      await api.delete(`/sf-master/${a.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Delete failed — deactivate instead"));
    }
  };

  const toggleActive = async (a) => {
    try {
      await api.put(`/sf-master/${a.id}`, { is_active: !(a.is_active !== false) });
      toast.success(a.is_active === false ? "Reactivated" : "Deactivated");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Toggle failed"); }
  };

  const exportCsv = () => {
    exportToCSV("sf-master.csv", filtered.map(a => ({
      sf_id: a.sf_id, sf_code: a.sf_code, process: a.process,
      spandex_denier: a.spandex_denier, stretch: a.stretch,
      rm_category: a.rm_category, yarn_denier: a.yarn_denier, colour: a.colour,
      covering_type: a.covering_type || "",
      spandex_pct: a.spandex_pct || 0, yarn_pct: a.yarn_pct || 0,
      output_uom: a.output_uom || "kg", hs_code: a.hs_code || "",
      description: a.description || "",
      status: a.is_active === false ? "Inactive" : "Active",
    })), [
      { key: "sf_id", label: "SF ID" }, { key: "sf_code", label: "SF Code" }, { key: "process", label: "Process" },
      { key: "spandex_denier", label: "Spandex Denier" }, { key: "stretch", label: "Stretch" },
      { key: "rm_category", label: "RM Category" }, { key: "yarn_denier", label: "Yarn Denier" },
      { key: "colour", label: "Colour" }, { key: "covering_type", label: "Covering" },
      { key: "spandex_pct", label: "Spandex %" }, { key: "yarn_pct", label: "Yarn %" },
      { key: "output_uom", label: "UOM" }, { key: "hs_code", label: "HS Code" },
      { key: "description", label: "Description" }, { key: "status", label: "Status" },
    ]);
  };

  return (
    <>
      <div className="p-6 space-y-5" data-testid="sf-master-page">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="overline mb-1">Product Masters · Semi-Finished</div>
            <h1 className="font-display text-3xl font-black tracking-tight">SF Master · Semi-Finished Codes</h1>
            <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
              Process-aware SF authoring. Pick a process; the <span className="font-mono text-emerald-400">sf_id</span> and
              <span className="font-mono text-emerald-400"> sf_code</span> compose automatically from the formula tokens and
              lock on save. Every SF you create here becomes selectable in BOM Master and Preparatory Entry pickers.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button data-testid="sf-warping-bom" onClick={() => setBomDialogOpen(true)}
              className={btnGhost + " border-purple-500 text-purple-300"}>
              <MagicWandIcon size={13} weight="bold" />Generate Warping from BOM
            </button>
            <button data-testid="sf-new" onClick={openNew} className={btnPrimary}>
              <PlusIcon size={13} weight="bold" />New SF Code
            </button>
            <button data-testid="sf-export-csv" onClick={exportCsv} className={btnGhost}>
              <DownloadSimpleIcon size={13} weight="bold" />Export CSV
            </button>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-3">
          <div className="tactical-card p-4"><div className="overline mb-1">Total SF Codes</div><div className="font-mono text-2xl font-semibold text-white">{list.length}</div></div>
          <div className="tactical-card p-4 border-l-2 border-l-emerald-500"><div className="overline text-emerald-300 mb-1">Active</div><div className="font-mono text-2xl font-semibold text-emerald-400">{activeCount}</div></div>
          <div className="tactical-card p-4 border-l-2 border-l-zinc-500"><div className="overline mb-1 text-zinc-400">Inactive</div><div className="font-mono text-2xl font-semibold text-zinc-500">{inactiveCount}</div></div>
          <div className="tactical-card p-4 border-l-2 border-l-purple-500">
            <div className="overline text-purple-300 mb-1">Processes</div>
            <div className="text-[10px] font-mono text-zinc-400 space-y-0.5">
              {PROCESSES.map(p => <div key={p}><span className="text-purple-300 font-mono">{PROCESS_PREFIX[p]}</span> · {p} ({list.filter(x => x.process === p).length})</div>)}
            </div>
          </div>
        </div>

        <div className="tactical-card p-4 space-y-3">
          <div className="relative">
            <MagnifyingGlassIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input data-testid="sf-search" value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search SF ID · SF Code · Description · RM Category · Colour…"
              className="w-full h-10 pl-10 pr-24 bg-[var(--bg-0)] border-2 border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]" />
          </div>
          <div className="flex items-center gap-4 flex-wrap text-[10px] font-mono uppercase tracking-widest">
            <div className="flex items-center gap-1">
              {["active", "inactive", "all"].map(s => (
                <button key={s} data-testid={`sf-status-${s}`} onClick={() => setStatusFilter(s)}
                  className={`h-8 px-3 rounded-sm border ${statusFilter === s ? "border-[var(--accent)] text-[var(--accent)]" : "border-[var(--border)] text-zinc-400 hover:text-white"}`}>{s}</button>
              ))}
            </div>
            <div className="flex items-center gap-1">
              <button data-testid="sf-process-all" onClick={() => setProcessFilter("")}
                className={`h-8 px-3 rounded-sm border ${processFilter === "" ? "border-[var(--accent)] text-[var(--accent)]" : "border-[var(--border)] text-zinc-400 hover:text-white"}`}>All</button>
              {PROCESSES.map(p => (
                <button key={p} data-testid={`sf-process-${PROCESS_PREFIX[p]}`} onClick={() => setProcessFilter(p)}
                  className={`h-8 px-3 rounded-sm border ${processFilter === p ? "border-purple-500 text-purple-300" : "border-[var(--border)] text-zinc-400 hover:text-white"}`}>{PROCESS_PREFIX[p]} · {p}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="tactical-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-2)]/40 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="px-3 py-2 text-left">SF ID</th>
                <th className="px-3 py-2 text-left">SF Code</th>
                <th className="px-3 py-2 text-left">Process</th>
                <th className="px-3 py-2 text-left">Composition</th>
                <th className="px-3 py-2 text-left">Description</th>
                <th className="px-3 py-2 text-left">UOM</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right w-32">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="sf-rows">
              {loading ? <tr><td colSpan={8} className="px-3 py-8 text-center text-zinc-500">Loading…</td></tr>
                : filtered.length === 0 ? <tr><td colSpan={8} className="px-3 py-8 text-center text-zinc-500" data-testid="sf-empty">No SF codes — click New SF Code to author one.</td></tr>
                  : filtered.map(a => (
                    <tr key={a.id} className="border-t border-[var(--border)] hover:bg-[var(--bg-2)]/30" data-testid={`sf-row-${a.sf_id || a.id.slice(0,8)}`}>
                      <td className="px-3 py-2 font-mono text-emerald-300 text-[11px]">{a.sf_id || "—"}</td>
                      <td className="px-3 py-2 font-mono text-[var(--accent)]" data-testid={`sf-code-${a.id.slice(0,8)}`}>{a.sf_code || a.product_code}</td>
                      <td className="px-3 py-2"><span className="inline-flex items-center px-2 py-0.5 rounded-sm border border-purple-500/40 text-purple-300 bg-purple-500/5 text-[10px] font-mono uppercase tracking-widest">{a.process || "—"}</span></td>
                      <td className="px-3 py-2 text-[11px] text-zinc-400 font-mono">
                        {[a.spandex_denier && `${a.spandex_denier}${String(a.spandex_denier).endsWith('D') ? '' : 'D'}`,
                          a.stretch && `${a.stretch}${String(a.stretch).endsWith('X') ? '' : 'X'}`,
                          a.rm_category, a.yarn_denier && `${a.yarn_denier}${String(a.yarn_denier).endsWith('D') ? '' : 'D'}`,
                          a.colour].filter(Boolean).join(" · ") || "—"}
                      </td>
                      <td className="px-3 py-2 text-zinc-300">{a.description || "—"}</td>
                      <td className="px-3 py-2 text-zinc-400 text-[11px]">{a.output_uom || "kg"}</td>
                      <td className="px-3 py-2">
                        <button data-testid={`sf-toggle-${a.id.slice(0,8)}`} onClick={() => toggleActive(a)}
                          className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${a.is_active === false ? "border-zinc-500/50 text-zinc-400" : "border-emerald-500/50 text-emerald-300 bg-emerald-500/5"}`}>
                          {a.is_active === false ? "Inactive" : "Active"}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-right space-x-1">
                        <RowActions
                          testid={`sf-actions-${a.id.slice(0,8)}`}
                          items={[
                            { label: "Edit", testid: `sf-edit-${a.id.slice(0,8)}`, onClick: () => openEdit(a) },
                            { label: "Delete", tone: "danger", testid: `sf-delete-${a.id.slice(0,8)}`, onClick: () => remove(a) },
                          ]}
                        />
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-3xl" data-testid="sf-editor-dialog"
          description="SF Master authoring — pick process, fill formula tokens, sf_code auto-composes and locks on save.">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? <>Edit SF · <span className="font-mono text-emerald-300">{editing.sf_id}</span></> : "New SF Code"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="border border-purple-500/40 bg-purple-500/5 rounded-sm p-3">
              <label className="text-[10px] uppercase tracking-widest text-purple-300 block mb-1">Process *</label>
              <div className="flex items-center gap-2 flex-wrap">
                {PROCESSES.map(p => (
                  <button key={p} data-testid={`sf-process-pick-${PROCESS_PREFIX[p]}`}
                    onClick={() => !editing && setForm(f => ({ ...f, process: p }))} disabled={!!editing}
                    className={`h-9 px-4 rounded-sm border text-[11px] font-mono uppercase tracking-widest ${form.process === p ? "border-purple-500 bg-purple-500/20 text-purple-200" : "border-[var(--border)] text-zinc-400 hover:text-white"} ${editing ? "opacity-60 cursor-not-allowed" : ""}`}>
                    <span className="text-emerald-300 mr-2">{PROCESS_PREFIX[p]}</span>{p}
                  </button>
                ))}
              </div>
              {editing && <div className="mt-2 text-[10px] font-mono text-amber-300 flex items-center gap-1.5"><LockKeyIcon size={10} weight="fill" /> Process is locked once the SF is created.</div>}
            </div>

            <div className="grid grid-cols-5 gap-3">
              {form.process !== "Warping" && <>
                <Field label="Spandex Denier *"><input data-testid="sf-spandex-denier" disabled={!!editing} value={form.spandex_denier} onChange={e => setForm(f => ({ ...f, spandex_denier: e.target.value.toUpperCase() }))} placeholder="e.g. 40" className={inp + (editing ? " opacity-60" : "")} /></Field>
                <Field label="Stretch *"><input data-testid="sf-stretch" disabled={!!editing} value={form.stretch} onChange={e => setForm(f => ({ ...f, stretch: e.target.value.toUpperCase() }))} placeholder="e.g. 3.5" className={inp + (editing ? " opacity-60" : "")} /></Field>
              </>}
              {form.process !== "Spandex Warping" && <>
                <Field label="RM Category *"><input data-testid="sf-rm-category" disabled={!!editing} value={form.rm_category} onChange={e => setForm(f => ({ ...f, rm_category: e.target.value.toUpperCase() }))} placeholder="e.g. NYLON" className={inp + (editing ? " opacity-60" : "")} /></Field>
                <Field label="Yarn Denier *"><input data-testid="sf-yarn-denier" disabled={!!editing} value={form.yarn_denier} onChange={e => setForm(f => ({ ...f, yarn_denier: e.target.value.toUpperCase() }))} placeholder="e.g. 70" className={inp + (editing ? " opacity-60" : "")} /></Field>
              </>}
              <Field label="Colour *"><input data-testid="sf-colour" disabled={!!editing} value={form.colour} onChange={e => setForm(f => ({ ...f, colour: e.target.value.toUpperCase() }))} placeholder="e.g. BLACK" className={inp + (editing ? " opacity-60" : "")} /></Field>
            </div>

            <div className="border border-emerald-500/40 bg-emerald-500/5 rounded-sm p-3 flex items-center justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-1 flex items-center gap-1.5"><LockKeyIcon size={10} weight="fill" /> Auto-Generated · Locked on Save</div>
                <div className="text-[10px] font-mono text-zinc-500">SF ID</div>
                <div className="font-mono text-lg text-emerald-300" data-testid="sf-preview-id">{editing ? editing.sf_id : `${composedPrefix}-<next>`}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] font-mono text-zinc-500">SF Code</div>
                <div className="font-mono text-lg text-[var(--accent)] tracking-wider" data-testid="sf-preview-code">{editing ? editing.sf_code : (composed || "— fill required tokens —")}</div>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-3">
              {form.process === "Covering" && (
                <Field label="Covering Type">
                  <select data-testid="sf-covering-type" value={form.covering_type} onChange={e => setForm(f => ({ ...f, covering_type: e.target.value }))} className={inp}>
                    <option value="">—</option><option>SINGLE</option><option>DOUBLE</option>
                  </select>
                </Field>
              )}
              <Field label="Spandex %"><input data-testid="sf-spandex-pct" type="number" step="0.1" value={form.spandex_pct} onChange={e => setForm(f => ({ ...f, spandex_pct: e.target.value }))} className={inpNum} /></Field>
              <Field label="Yarn %"><input data-testid="sf-yarn-pct" type="number" step="0.1" value={form.yarn_pct} onChange={e => setForm(f => ({ ...f, yarn_pct: e.target.value }))} className={inpNum} /></Field>
              <Field label="Output UOM">
                <select data-testid="sf-uom" value={form.output_uom} onChange={e => setForm(f => ({ ...f, output_uom: e.target.value }))} className={inp}>
                  <option value="kg">kg</option><option value="m">m</option><option value="pcs">pcs</option>
                </select>
              </Field>
              <Field label="HS Code"><input data-testid="sf-hs" value={form.hs_code} onChange={e => setForm(f => ({ ...f, hs_code: e.target.value }))} className={inp + " font-mono"} /></Field>
              <div className="col-span-4">
                <Field label="Description"><input data-testid="sf-desc" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional — defaults to the SF Code" className={inp} /></Field>
              </div>
              <div className="col-span-4 flex items-center gap-2">
                <input id="sf-active" data-testid="sf-active" type="checkbox" checked={!!form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
                <label htmlFor="sf-active" className="text-xs text-zinc-300">Active (available for new BOMs)</label>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <button data-testid="sf-cancel" onClick={() => setDialogOpen(false)} className={btnGhost}><XIcon size={13} /> Cancel</button>
            <button data-testid="sf-save" onClick={save} disabled={saving} className={btnPrimary}><FloppyDiskIcon size={13} weight="bold" />{saving ? "Saving…" : (editing ? "Save Changes" : "Create SF")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 3J.6 · BOM-driven Warping SFG generator */}
      <Dialog open={bomDialogOpen} onOpenChange={(v) => { setBomDialogOpen(v); if (!v) { setBomPreview(null); setBomResult(null); setSelectedCwoId(""); setSelectedLineIds({}); } }}>
        <DialogContent className="max-w-4xl" data-testid="sf-warping-bom-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MagicWandIcon size={18} weight="bold" className="text-purple-400" />
              Generate Warping SFG from Main BOM
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="overline block mb-1">A · Source · Warping CWO</label>
              <div className="flex gap-2">
                <select
                  data-testid="sf-warping-bom-cwo-select"
                  value={selectedCwoId}
                  onChange={async (e) => {
                    const v = e.target.value; setSelectedCwoId(v); setBomPreview(null); setBomResult(null); setSelectedLineIds({});
                    if (!v) return;
                    setBomLoading(true);
                    try {
                      const { data } = await api.get(`/sf-master/warping/preview-from-cwo/${v}`);
                      setBomPreview(data);
                      const init = {}; (data.lines || []).forEach(ln => { if (ln.valid && !ln.existing_sf) init[ln.bom_line_id] = true; });
                      setSelectedLineIds(init);
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Failed to load BOM preview");
                    } finally { setBomLoading(false); }
                  }}
                  className={inp + " flex-1"}
                >
                  <option value="">— select a Warping CWO —</option>
                  {warpingCwos.map(w => <option key={w.id} value={w.id}>{w.cwo_no} · {w.parent_wo_no} · {w.source_order_no || "—"}</option>)}
                </select>
                <button data-testid="sf-warping-bom-refresh" onClick={async () => {
                  try {
                    const { data } = await api.get("/child-work-orders");
                    const rows = Array.isArray(data) ? data : (data.rows || []);
                    setWarpingCwos(rows.filter(r => (r.stage_code || "").toUpperCase() === "WARPING"));
                  } catch { toast.error("Failed to load Warping CWOs"); }
                }} className={btnGhost}>Refresh</button>
              </div>
            </div>

            {bomLoading && <div className="text-xs text-zinc-400 font-mono">Loading BOM…</div>}

            {bomPreview && (
              <>
                <div className="tactical-card p-3 text-xs font-mono text-zinc-300 space-y-1">
                  <div><span className="text-zinc-500">Article:</span> {bomPreview.bom.article_number}</div>
                  <div><span className="text-zinc-500">BOM Version:</span> v{bomPreview.bom.version}</div>
                  <div><span className="text-zinc-500">WO / CWO:</span> {bomPreview.wo.wo_no} / {bomPreview.cwo.cwo_no}</div>
                </div>

                <div>
                  <label className="overline block mb-1">B · Warping BOM Lines · {bomPreview.lines.length} row(s)</label>
                  <div className="tactical-card overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-[var(--bg-2)]">
                        <tr className="text-left overline">
                          <th className="p-2 w-8">Pick</th>
                          <th className="p-2">RM Code</th>
                          <th className="p-2 w-24 text-right">Ends</th>
                          <th className="p-2 w-16">UOM</th>
                          <th className="p-2">Generated SFG Code</th>
                          <th className="p-2 w-24">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bomPreview.lines.map(ln => (
                          <tr key={ln.bom_line_id} className="border-t border-[var(--border)]" data-testid={`sf-warping-bom-line-${ln.bom_line_id}`}>
                            <td className="p-2">
                              <input type="checkbox" data-testid={`sf-warping-bom-pick-${ln.bom_line_id}`}
                                disabled={!ln.valid}
                                checked={!!selectedLineIds[ln.bom_line_id]}
                                onChange={(e) => setSelectedLineIds(v => ({ ...v, [ln.bom_line_id]: e.target.checked }))} />
                            </td>
                            <td className="p-2 font-mono text-zinc-200 truncate max-w-xs" title={ln.rm_code}>{ln.rm_code}</td>
                            <td className="p-2 font-mono tabular-nums text-right">{ln.no_of_ends}</td>
                            <td className="p-2 font-mono">{ln.uom}</td>
                            <td className="p-2 font-mono text-purple-300 truncate max-w-md" title={ln.sf_code_preview}>{ln.sf_code_preview || <span className="text-red-400">— invalid —</span>}</td>
                            <td className="p-2 font-mono text-[10px] uppercase tracking-widest">
                              {!ln.valid ? <span className="text-red-400">{ln.invalid_reason}</span>
                                : ln.existing_sf ? <span className="text-amber-300">EXISTS · {ln.existing_sf.sf_id}</span>
                                : <span className="text-emerald-300">NEW</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {bomResult && (
              <div className="tactical-card p-3 space-y-1">
                <div className="overline">C · Result</div>
                <div className="text-xs font-mono">
                  <span className="text-emerald-400">{bomResult.created_count} created</span>
                  {" · "}<span className="text-amber-300">{bomResult.reused_count} reused</span>
                  {" · "}<span className="text-red-400">{bomResult.error_count} error(s)</span>
                </div>
                {(bomResult.results || []).filter(r => r.verdict === "ERROR").map((r,i) => (
                  <div key={i} className="text-[10px] text-red-400 font-mono">Line {r.bom_line_id}: {r.error}</div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <button data-testid="sf-warping-bom-cancel" onClick={() => setBomDialogOpen(false)} className={btnGhost}><XIcon size={13} /> Close</button>
            <button data-testid="sf-warping-bom-generate"
              onClick={async () => {
                const ids = Object.entries(selectedLineIds).filter(([, v]) => v).map(([k]) => k);
                if (!selectedCwoId || ids.length === 0) { toast.error("Pick at least one BOM line"); return; }
                setBomGenerating(true);
                try {
                  const { data } = await api.post("/sf-master/warping/generate-from-bom", { cwo_id: selectedCwoId, bom_line_ids: ids });
                  setBomResult(data);
                  toast.success(`${data.created_count} created · ${data.reused_count} reused · ${data.error_count} error(s)`);
                  await load();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Failed to generate");
                } finally { setBomGenerating(false); }
              }}
              disabled={bomGenerating}
              className={btnPrimary}>
              <MagicWandIcon size={13} weight="bold" />{bomGenerating ? "Generating…" : "Generate & Link"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">{label}</label>
      {children}
    </div>
  );
}
